import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle, Check, CheckSquare, Eye, ImageIcon, Loader2,
  Megaphone, Pencil, Plus, Save, Search, Square, Trash2,
  UploadCloud, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/ui/rich-editor";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBulkDeleteNews, useCreateNews, useDeleteNews, useNews,
  useNewsItem, useUpdateNews, type NewsRow, type NewsStatus,
} from "@/hooks/useNews";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

/**
 * /news — CEO + Admin author official news items. The page is hidden
 * from non-CEO users via the Sidebar gate, and the API enforces
 * IsCeoLevel on every write action.
 */
export default function News() {
  const [searchDraft, setSearchDraft] = useState("");
  const [search,      setSearch]      = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | NewsStatus>("");
  const [page,        setPage]        = useState(1);
  const [createOpen,  setCreateOpen]  = useState(false);
  const [editingId,   setEditingId]   = useState<string | null>(null);

  const list = useNews({
    search: search || undefined,
    status: statusFilter || undefined,
    page,
  });
  const create = useCreateNews();
  const update = useUpdateNews();
  const del    = useDeleteNews();
  const bulkDelete = useBulkDeleteNews();
  const confirm = useConfirm();

  // Multi-select
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const bulkBusy = bulkDelete.isPending;
  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  const rows = list.data?.results ?? [];
  const totalCount = list.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  function selectAllVisible() {
    setSelected(new Set([...selected, ...visibleIds]));
  }

  useEffect(() => {
    if (selected.size === 0) return;
    const existing = new Set(rows.map((r) => r.id));
    const trimmed = new Set([...selected].filter((id) => existing.has(id)));
    if (trimmed.size !== selected.size) setSelected(trimmed);
  }, [rows, selected]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && selected.size > 0 && !bulkBusy) clearSelection();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size, bulkBusy]);

  async function onDelete(row: NewsRow) {
    const ok = await confirm({
      title: `Delete "${row.title}"?`,
      description: "This will remove the news post from the public site immediately. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(row.id);
  }

  async function onBulkDelete() {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} news post${selected.size === 1 ? "" : "s"}?`,
      description: "Selected posts will be removed from the public site immediately. This cannot be undone.",
      confirmLabel: `Delete ${selected.size}`,
      destructive: true,
    });
    if (!ok) return;
    await bulkDelete.mutateAsync([...selected]);
    clearSelection();
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <Megaphone className="h-3.5 w-3.5" />Public news
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">News</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Official communications for the public{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/news/</code>{" "}
            page. CEO and Admin only.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />New post
        </Button>
      </header>

      {/* Search + status filter */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search title or content…"
              className="h-10 pl-9"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
          <div className="flex items-center gap-1 rounded-md border border-border/60 bg-card p-1">
            {(["", "published", "draft"] as const).map((s) => (
              <button
                key={s || "all"}
                type="button"
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={cn(
                  "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                  statusFilter === s
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                {s === "" ? "All" : s === "published" ? "Published" : "Drafts"}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-30 -mx-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-primary/10">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckSquare className="h-4 w-4" />
            {selected.size} selected
          </div>
          <Button variant="ghost" size="sm" onClick={selectAllVisible} disabled={bulkBusy || allVisibleSelected} className="h-7">
            Select all visible
          </Button>
          <div className="mx-1 hidden h-5 w-px bg-border/60 sm:block" />
          <Button
            variant="outline" size="sm"
            onClick={onBulkDelete} disabled={bulkBusy}
            className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkBusy} className="ml-auto h-7">
            {bulkBusy
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Working…</>
              : <><X className="mr-1.5 h-3.5 w-3.5" />Clear</>}
          </Button>
        </div>
      )}

      {/* List */}
      {list.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Megaphone className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold">
              {search ? "No news matches your search" : "No news posts yet"}
            </h3>
            <p className="max-w-sm text-xs text-muted-foreground">
              {search
                ? "Try a different keyword or clear the search."
                : "Publish the first post to populate the public news feed."}
            </p>
            {!search && (
              <Button onClick={() => setCreateOpen(true)} className="mt-2">
                <Plus className="mr-2 h-4 w-4" />New post
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/60 shadow-soft">
          <CardContent className="p-0">
            <ul className="divide-y divide-border/60">
              {rows.map((row) => (
                <NewsRowItem
                  key={row.id}
                  row={row}
                  isSelected={selected.has(row.id)}
                  anySelected={selected.size > 0}
                  onToggleSelect={() => toggleSelect(row.id)}
                  onEdit={() => setEditingId(row.id)}
                  onDelete={() => onDelete(row)}
                  onTogglePublish={() => update.mutate({
                    id: row.id,
                    status: row.status === "published" ? "draft" : "published",
                  })}
                  busy={del.isPending || update.isPending || bulkBusy}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> · <strong>{totalCount}</strong> post{totalCount === 1 ? "" : "s"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <NewsFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        editingId={null}
        onSubmit={async (input) => {
          await create.mutateAsync(input);
          setCreateOpen(false);
        }}
        busy={create.isPending}
        error={extractError(create.error)}
      />
      <NewsFormDialog
        open={editingId !== null}
        onOpenChange={(o) => !o && setEditingId(null)}
        editingId={editingId}
        onSubmit={async (input) => {
          if (!editingId) return;
          await update.mutateAsync({ id: editingId, ...input });
          setEditingId(null);
        }}
        busy={update.isPending}
        error={extractError(update.error)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function NewsRowItem({
  row, isSelected, anySelected, onToggleSelect, onEdit, onDelete, onTogglePublish, busy,
}: {
  row: NewsRow;
  isSelected: boolean;
  anySelected: boolean;
  onToggleSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePublish: () => void;
  busy: boolean;
}) {
  const isDraft = row.status === "draft";
  return (
    <li className={cn(
      "group flex items-start gap-3 p-4 transition-colors hover:bg-muted/30",
      isSelected && "bg-primary/5",
      isDraft && "opacity-80",
    )}>
      <button
        type="button"
        onClick={onToggleSelect}
        aria-label={isSelected ? "Deselect" : "Select"}
        aria-pressed={isSelected}
        disabled={busy}
        className={cn(
          "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
          isSelected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border/70 bg-card text-muted-foreground hover:border-primary/50",
          anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          busy && "cursor-not-allowed opacity-50",
        )}
      >
        {isSelected ? <Check className="h-3 w-3" /> : <Square className="h-3 w-3 stroke-[1.6]" />}
      </button>

      {/* Thumbnail */}
      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-muted">
        {row.image_url ? (
          <img src={row.image_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h4 className="truncate text-sm font-semibold text-foreground">{row.title}</h4>
          {isDraft ? (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
              Draft
            </Badge>
          ) : (
            <Badge className="border-0 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300">
              Published
            </Badge>
          )}
          {row.external_source_name && (
            <Badge variant="outline" className="font-mono text-[10px]">
              ext: {row.external_source_name}
            </Badge>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{formatDateTime(row.created_at)}</span>
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" />{row.view_count} view{row.view_count === 1 ? "" : "s"}
          </span>
          <span className="font-mono text-[10px] opacity-70">/{row.slug}</span>
        </div>
      </div>

      {/* Hover actions */}
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <Button
          variant={isDraft ? "default" : "outline"}
          size="sm"
          onClick={onTogglePublish}
          disabled={busy}
          aria-label={isDraft ? "Publish post" : "Move to draft"}
          title={isDraft ? "Publish to the public /news/ page" : "Move back to draft (hide from public)"}
        >
          {isDraft ? "Publish" : "Unpublish"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit} disabled={busy} aria-label="Edit post">
          <Pencil className="h-3.5 w-3.5" />Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} disabled={busy} aria-label="Delete post"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />Delete
        </Button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit dialog
// ---------------------------------------------------------------------------
interface FormInput {
  title: string;
  content: string;
  status: NewsStatus;
  image?: File;
}

function NewsFormDialog({
  open, onOpenChange, editingId, onSubmit, busy, error,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editingId: string | null;
  onSubmit: (input: FormInput) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const isEdit = !!editingId;
  const existing = useNewsItem(editingId ?? undefined);

  const [title, setTitle]     = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus]   = useState<NewsStatus>("draft");
  const [file, setFile]       = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Reseed from server when editing target changes
  useEffect(() => {
    if (!open) return;
    if (isEdit && existing.data) {
      setTitle(existing.data.title);
      setContent(existing.data.content);
      setStatus(existing.data.status);
      setFile(null);
      setPreview(null);
    } else if (!isEdit) {
      setTitle("");
      setContent("");
      setStatus("draft");
      setFile(null);
      setPreview(null);
    }
  }, [open, isEdit, existing.data]);

  // Preview the newly picked file
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const canSubmit = title.trim().length >= 4 && content.replace(/<[^>]+>/g, "").trim().length >= 10;

  function handleSubmit(submitStatus: NewsStatus) {
    if (!canSubmit) return;
    onSubmit({
      title: title.trim(),
      content,
      status: submitStatus,
      image: file ?? undefined,
    });
  }

  const showImage = preview || existing.data?.image_url;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? "Edit news post" : "New news post"}
            {isEdit && (status === "published"
              ? <Badge className="border-0 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Published</Badge>
              : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">Draft</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Drafts are visible only to staff. Publishing makes the post
            live on the public{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-[10px]">/news/</code>{" "}
            page immediately.
          </DialogDescription>
        </DialogHeader>

        {isEdit && existing.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="news-title">Title</Label>
              <Input
                id="news-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Ethiopia ratifies the COP30 outcomes"
                maxLength={255}
                disabled={busy}
                autoFocus={!isEdit}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Cover image (optional)</Label>
              {showImage ? (
                <div className="overflow-hidden rounded-lg border border-border/60">
                  <img src={preview || existing.data?.image_url || ""} alt="Cover preview" className="max-h-56 w-full bg-muted object-contain" />
                </div>
              ) : null}
              <label
                htmlFor="news-image"
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/60",
                  busy && "pointer-events-none opacity-60",
                )}
              >
                <UploadCloud className="h-4 w-4" />
                {file
                  ? <span className="font-medium text-foreground">Will use: {file.name}</span>
                  : showImage
                    ? <span>Replace cover image</span>
                    : <span>Click to upload a cover image</span>}
              </label>
              <input
                id="news-image"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={busy}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="news-content">Body</Label>
              <RichEditor
                value={content}
                onChange={setContent}
                placeholder="Write the announcement…"
                disabled={busy}
                minHeightClass="min-h-[260px]"
              />
            </div>

            {error && (
              <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </p>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              {/* Two submit paths: keep as draft, or publish. On edit the
                  buttons preserve the current status and offer the toggle. */}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSubmit("draft")}
                disabled={!canSubmit || busy}
              >
                {busy
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Save className="mr-2 h-4 w-4" />}
                Save as draft
              </Button>
              <Button
                type="button"
                onClick={() => handleSubmit("published")}
                disabled={!canSubmit || busy}
              >
                {busy
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Save className="mr-2 h-4 w-4" />}
                {isEdit && status === "published" ? "Save (stay published)" : "Publish"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function extractError(err: unknown): string | null {
  if (!err) return null;
  const e = err as { response?: { data?: { detail?: string; title?: string[]; content?: string[]; image?: string[] } } };
  return (
    e.response?.data?.detail
    || e.response?.data?.title?.[0]
    || e.response?.data?.content?.[0]
    || e.response?.data?.image?.[0]
    || (err as Error).message
    || "Something went wrong."
  );
}
